import React, { useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw, Download, LucideIcon } from "lucide-react";
import ReactECharts from 'echarts-for-react';
import { useRawDataChart } from './hooks/useRawDataChart';
import { useIsMobile } from '@/hooks/use-mobile';

export interface TemperatureAnnotation {
  name: string;
  value: number; // Temperature in Fahrenheit
  color: string; // Hex color code
  lineStyle?: 'solid' | 'dashed' | 'dotted';
}

export interface RawDataChartConfig {
  title: string;
  icon?: LucideIcon;
  showInlet?: boolean;
  showOutlet?: boolean;
  showAmbient?: boolean;
  showPower?: boolean;
  showCurrent?: boolean;
  annotations?: TemperatureAnnotation[];
  downloadFileName?: string;
  inletColor?: string;
  outletColor?: string;
  ambientColor?: string;
  powerColor?: string;
  currentColor?: string;
}

export interface GenericRawDataChartProps {
  installationId: string;
  organizationId: number;
  plotStartTime: string;
  plotEndTime: string;
  config: RawDataChartConfig;
  onSuccess?: (hasData: boolean) => void;
  onRefresh?: () => void;
}

const GenericRawDataChart: React.FC<GenericRawDataChartProps> = ({
  installationId,
  organizationId,
  plotStartTime,
  plotEndTime,
  config,
  onSuccess,
  onRefresh
}) => {
  const isMobile = useIsMobile();
  const chartRef = useRef<any>(null);
  const [electricalMetric, setElectricalMetric] = useState<'power' | 'current'>('power');

  const {
    showInlet = true,
    showOutlet = true,
    showAmbient = false,
    showPower = true,
    showCurrent = true,
    annotations = [],
    downloadFileName = 'raw-data-chart',
    inletColor = '#10b981',
    outletColor = '#001F5C',
    ambientColor = '#f59e0b',
    powerColor = '#D81B60',
    currentColor = '#D81B60'
  } = config;

  const { data, loading, error, refresh } = useRawDataChart({
    installationId,
    organizationId,
    plotStartTime,
    plotEndTime,
    showAmbient: showAmbient || config.showAmbient,
    showCurrent: showCurrent || config.showCurrent,
    onSuccess
  });

  const handleDownload = () => {
    if (chartRef.current) {
      const chartInstance = chartRef.current.getEchartsInstance();
      const url = chartInstance.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff'
      });
      
      const link = document.createElement('a');
      link.download = `${downloadFileName}-${new Date().toISOString().split('T')[0]}.png`;
      link.href = url;
      link.click();
    }
  };

  const handleRefresh = () => {
    refresh();
    if (onRefresh) {
      onRefresh();
    }
  };

  // Calculate min/max for axes
  const allTemps = [
    ...(showInlet ? data.inlet.map(d => d[1]) : []),
    ...(showOutlet ? data.outlet.map(d => d[1]) : []),
    ...(showAmbient ? data.ambient.map(d => d[1]) : []),
    ...(annotations.map(a => a.value))
  ];
  const minTemp = allTemps.length > 0 ? Math.min(...allTemps) : 0;
  const maxTemp = allTemps.length > 0 ? Math.max(...allTemps) : 100;
  const minPower = data.power.length > 0 ? Math.min(...data.power.map(d => d[1])) : 0;
  const maxPower = data.power.length > 0 ? Math.max(...data.power.map(d => d[1])) : 1000;
  const minCurrent = data.current.length > 0 ? Math.min(...data.current.map(d => d[1])) : 0;
  const maxCurrent = data.current.length > 0 ? Math.max(...data.current.map(d => d[1])) : 100;

  const hasPowerData = showPower && data.power.length > 0 && maxPower > 10;
  const hasCurrentData = showCurrent && data.current.length > 0;
  const hasElectricalData = hasPowerData || hasCurrentData;
  const showElectricalAxis = hasElectricalData;

  // Temperature axis configuration (shared between single and dual axis modes)
  const temperatureAxisConfig = {
    type: 'value' as const,
    name: 'Temperature (°F)',
    nameLocation: 'middle' as const,
    nameGap: isMobile ? 75 : 50,
    nameRotate: 90,
    position: 'left' as const,
    min: Math.min(0, minTemp - 5),
    max: maxTemp + 10,
    splitNumber: isMobile ? 5 : 8,
    axisLabel: {
      fontSize: isMobile ? 11 : 12,
      margin: isMobile ? 12 : 8,
      formatter: (value: number) => value.toFixed(1)
    }
  };

  const IconComponent = config.icon;

  const legendData: string[] = [];
  if (showInlet) legendData.push('Compressor Inlet');
  if (showOutlet) legendData.push('Compressor Outlet');
  if (showAmbient) legendData.push('Ambient');
  if (electricalMetric === 'power' && hasPowerData) legendData.push('Power');
  if (electricalMetric === 'current' && hasCurrentData) legendData.push('Current');

  const series: any[] = [];
  
  if (showInlet) {
    series.push({
      name: 'Compressor Inlet',
      type: 'line',
      yAxisIndex: 0,
      data: data.inlet,
      smooth: true,
      symbol: 'none',
      lineStyle: { width: 2, color: inletColor },
      itemStyle: { color: inletColor }
    });
  }

  if (showOutlet) {
    series.push({
      name: 'Compressor Outlet',
      type: 'line',
      yAxisIndex: 0,
      data: data.outlet,
      smooth: true,
      symbol: 'none',
      lineStyle: { width: 2, color: outletColor },
      itemStyle: { color: outletColor }
    });
  }

  if (showAmbient) {
    series.push({
      name: 'Ambient',
      type: 'line',
      yAxisIndex: 0,
      data: data.ambient,
      smooth: true,
      symbol: 'none',
      lineStyle: { width: 2, color: ambientColor },
      itemStyle: { color: ambientColor }
    });
  }

  if (electricalMetric === 'power' && hasPowerData) {
    series.push({
      name: 'Power',
      type: 'line',
      yAxisIndex: showElectricalAxis ? 1 : 0,
      data: data.power,
      smooth: true,
      symbol: 'none',
      lineStyle: { width: 2, color: powerColor },
      itemStyle: { color: powerColor }
    });
  }

  if (electricalMetric === 'current' && hasCurrentData) {
    series.push({
      name: 'Current',
      type: 'line',
      yAxisIndex: showElectricalAxis ? 1 : 0,
      data: data.current,
      smooth: true,
      symbol: 'none',
      lineStyle: { width: 2, color: currentColor },
      itemStyle: { color: currentColor }
    });
  }

  // Add annotation lines (always visible, not in legend)
  series.push(...annotations.map(annotation => ({
    name: annotation.name,
    type: 'line' as const,
    markLine: {
      silent: true,
      symbol: 'none',
      label: { 
        position: 'insideStartTop' as const, 
        formatter: isMobile ? '' : annotation.name,
        show: !isMobile
      },
      lineStyle: { 
        color: annotation.color, 
        type: annotation.lineStyle || 'dashed', 
        width: 2 
      },
      data: [{ yAxis: annotation.value }]
    }
  })));

  const option = {
    title: { show: false },
    legend: {
      data: legendData,
      top: isMobile ? 0 : 10,
      left: 'center',
      itemGap: isMobile ? 6 : 20,
      textStyle: { fontSize: isMobile ? 8 : 12 },
      selected: {
        ...(showInlet ? { 'Compressor Inlet': true } : {}),
        ...(showOutlet ? { 'Compressor Outlet': true } : {}),
        ...(showAmbient ? { 'Ambient': true } : {}),
        ...(electricalMetric === 'power' && hasPowerData ? { 'Power': true } : {}),
        ...(electricalMetric === 'current' && hasCurrentData ? { 'Current': true } : {})
      },
      selectedMode: 'multiple' as const
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params: any) {
        if (Array.isArray(params)) {
          const time = new Date(params[0].data[0]).toLocaleString();
          let tooltip = `<b>Time:</b> ${time}<br/>`;
          params.forEach((param: any) => {
            if (param.seriesName === 'Compressor Inlet') {
              tooltip += `<b>Inlet:</b> ${param.data[1]?.toFixed(1) || 'N/A'}°F<br/>`;
            } else if (param.seriesName === 'Compressor Outlet') {
              tooltip += `<b>Outlet:</b> ${param.data[1]?.toFixed(1) || 'N/A'}°F<br/>`;
            } else if (param.seriesName === 'Ambient') {
              tooltip += `<b>Ambient:</b> ${param.data[1]?.toFixed(1) || 'N/A'}°F<br/>`;
            } else if (param.seriesName === 'Power') {
              tooltip += `<b>Power:</b> ${param.data[1]?.toFixed(1) || 'N/A'}W<br/>`;
            } else if (param.seriesName === 'Current') {
              tooltip += `<b>Current:</b> ${param.data[1]?.toFixed(1) || 'N/A'}A<br/>`;
            }
          });
          return tooltip;
        }
        return '';
      }
    },
    grid: {
      left: isMobile ? '18%' : '3%',
      right: isMobile ? '15%' : '4%',
      bottom: isMobile ? '25%' : '12%',
      top: isMobile ? '25%' : '18%',
      containLabel: true
    },
    xAxis: {
      type: 'time',
      name: 'Time',
      nameLocation: 'middle',
      nameGap: isMobile ? 40 : 35,
      axisLabel: {
        formatter: function(value: number) {
          const date = new Date(value);
          if (isMobile) {
            return date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
          }
          return date.toLocaleDateString() + '\n' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
        },
        rotate: isMobile ? 45 : 0,
        margin: isMobile ? 15 : 12,
        interval: 'auto',
        fontSize: isMobile ? 10 : 11
      },
      axisTick: { alignWithLabel: true },
      splitLine: { show: false }
    },
    yAxis: showElectricalAxis ? [
      temperatureAxisConfig,
      {
        type: 'value',
        name: electricalMetric === 'power' ? 'Power (W)' : 'Current (A)',
        nameLocation: 'middle',
        nameGap: isMobile ? 75 : 50,
        nameRotate: -90,
        position: 'right',
        min: electricalMetric === 'power' ? Math.max(0, minPower - 100) : Math.max(0, minCurrent - 5),
        max: electricalMetric === 'power' ? maxPower + 100 : maxCurrent + 10,
        splitNumber: isMobile ? 5 : 8,
        axisLabel: {
          fontSize: isMobile ? 11 : 12,
          margin: isMobile ? 12 : 8,
          formatter: (value: number) => electricalMetric === 'power' ? value.toFixed(0) : value.toFixed(1)
        }
      }
    ] : temperatureAxisConfig,
    series,
    dataZoom: isMobile ? [] : [
      {
        type: 'slider',
        start: 0,
        end: 100,
        bottom: 10,
        height: 20,
        handleIcon: 'M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23.1h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
        handleSize: '80%',
        handleStyle: {
          color: '#fff',
          shadowBlur: 3,
          shadowColor: 'rgba(0, 0, 0, 0.6)',
          shadowOffsetX: 2,
          shadowOffsetY: 2
        }
      }
    ],
    animation: true,
    animationDuration: 1000
  };

  const hasData = data.inlet.length > 0 || data.outlet.length > 0 || data.ambient.length > 0 || data.power.length > 0 || data.current.length > 0;

  return (
    <Card className="p-6 mb-6 col-span-12">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            {IconComponent && <IconComponent className="h-5 w-5" />}
            {config.title}
          </CardTitle>
          <div className="flex items-center gap-2">
            {hasElectricalData && hasPowerData && hasCurrentData && (
              <div className="flex items-center border rounded-md overflow-hidden">
                <Button
                  variant={electricalMetric === 'power' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setElectricalMetric('power')}
                  className="rounded-none text-xs px-2"
                >
                  Power
                </Button>
                <Button
                  variant={electricalMetric === 'current' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setElectricalMetric('current')}
                  className="rounded-none text-xs px-2"
                >
                  Current
                </Button>
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              disabled={!hasData}
              title="Download chart"
            >
              <Download className="h-3 w-3" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {error && !hasData ? (
          <div className="text-center py-8">
            <div className="text-red-500 mb-2">Error loading data</div>
            <div className="text-sm text-muted-foreground">{error}</div>
            <Button variant="outline" onClick={handleRefresh} className="mt-4">
              Try Again
            </Button>
          </div>
        ) : loading && !hasData ? (
          <div className="text-center py-8">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
            <div className="text-muted-foreground">Loading data...</div>
          </div>
        ) : !hasData ? (
          <div className="text-center py-8">
            <div className="text-muted-foreground">No data found for this period</div>
            {error && (
              <div className="text-sm text-red-500 mt-2">{error}</div>
            )}
          </div>
        ) : (
          <div className="h-80">
            <ReactECharts
              ref={chartRef}
              key={`chart-${showElectricalAxis}-${electricalMetric}`}
              option={option}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GenericRawDataChart;

